import "core-js/stable";
import "react-app-polyfill/ie11";
import "whatwg-fetch";

import React, { useState, useEffect } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import { Router } from "./services/Router";
import "./index.css";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import "typeface-roboto";
import URI from "urijs";
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material/styles";
import { Provider } from "react-redux";
import { CookiesProvider } from "react-cookie";
import { createStore, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import rootReducer from "./reducers";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import ISXUtils, { ISXContext } from "services/Utils";
import QueryService from "services/QueryService.js";
import Widgets from "containers/widgets/AllWidgets";

// const uri = new URI("https://dev.mystacks.interstacks.com");
const uri = new URI(window.location);
const subdomain = uri.subdomain();

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const middleware = applyMiddleware(thunk);
const store = createStore(rootReducer, composeEnhancers(middleware));

const AppWrapper = () => {
  const location = useLocation();
  // updates the state when a new update is pending.
  // action for updating the service worker.
  const updateFrontEnd = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          // send the skip message to kick off the service worker install.
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          // add an listener to reload page when the new service worker is ready.
          registration.waiting.addEventListener("statechange", (event) => {
            const { state = "" } = event.target as any || {};
            if (state === "activated") {
              console.log("reloading");
              serviceWorkerRegistration.unregister();
              //console.log("unregistering.. do I need to?");
              window.location.reload();
            }
          });
        }
      });
    }
  };
  // effect added from router location to check for a new service worker on every
  // page transition (change of route).
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
        console.log("registration update j");
      });
    }
  }, [location]);

  serviceWorkerRegistration.register({ onUpdate: updateFrontEnd });

  const [bespokeContent, setBespokeContent] = useState(null);

  useEffect(() => {
    const subdomainParts = subdomain && subdomain.split(".");
    if (subdomainParts && subdomainParts.length > 0) {
      const themeids = subdomainParts
        .map((p, i) => subdomainParts.slice(i).join("."))
        .reverse();
      (async () => {
        try {
          const results = await QueryService.themesQuery(themeids);
          const combinedResults = results.reduce((acc, content) => {
            Object.assign(acc, content);
            return acc;
          }, {});
          setBespokeContent(combinedResults);
        } catch (e) {
          console.error("themes query exception", e);
          // can make no further progress, since we don't want to show
          // the default theme when they are expecting another; perhaps
          // we should have a _very_ generic default and make the current
          // default theme the one for "mystacks"...
        }
      })();
    } else {
      setBespokeContent({});
    }
  }, []);

  if (!bespokeContent) {
    return null;
  }

  const bespokeTheme = bespokeContent.theme || {};

  const theme = createTheme({
    palette: {
      primary: {
        main: "#E28C05",
        contrastText: "#f8f8f8",
      },
      secondary: {
        main: "#E28C05",
      },
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.54)",
        disabled: "rgba(0, 0, 0, 0.38)",
        hint: "rgba(0, 0, 0, 0.38)",
      },
    },
    widget: {
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.54)",
      },
    },
    components: {
      MuiInputBase: {
        defaultProps: {
          disableInjectingGlobalStyles: true,
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 24,
            paddingRight: 56,
          },
          head: {
            lineHeight: "1em",
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: "auto",
            marginRight: 16,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          inset: {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: {
            fontSize: "0.875rem",
          },
        },
      },
      MUIDataTable: {
        styleOverrides: {
          responsiveScrollMaxHeight: {
            maxHeight: "none",
          },
        },
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          root: {
            fontSize: 13,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            fontSize: 16,
            fontWeight: 800,
            color: "rgba(0, 0, 0, 0.87)",
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            minWidth: 26,
          },
        },
      },
      MUIDataTableSelectCell: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
          },
        },
      },
      MUIDataTableFilterList: {
        styleOverrides: {
          root: {
            flex: "0 0 auto",
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            minHeight: 48,
          },
          icon: {
            padding: 6,
          },
          iconActive: {
            padding: 6,
          },
        },
      },
      MUIDataTableSearch: {
        styleOverrides: {
          clearIcon: {
            padding: 6,
          },
        },
      },
      MuiTreeItem: {
        styleOverrides: {
          content: {
            padding: 0,
          },
          label: {
            fontSize: "0.875rem",
          },
        },
      },
    },
    ...((bespokeTheme && bespokeTheme.styling) || {}),
  });

  const isxContext: any = {
    logo: bespokeTheme.logo,
    labels: {
      organization:
        (bespokeTheme.labels && bespokeTheme.labels.organization) ||
        "Interstacks",
      platform: bespokeTheme.labels?.platform ?? "MyStacks",
      device: ISXUtils.getDeviceLabel(bespokeTheme),
      devicePlural: ISXUtils.getDeviceLabel(bespokeTheme, false, true),
      deviceCapitalized: ISXUtils.getDeviceLabel(bespokeTheme, true),
      deviceCapitalizedPlural: ISXUtils.getDeviceLabel(
        bespokeTheme,
        true,
        true
      ),
    },
    termsOfUse: bespokeContent.termsOfUse,
    smsTermsUse:
      bespokeContent.smsTermsOfUse ?? "https://interstacks.com/sms-terms",
    privacyPolicy: bespokeContent.privacyPolicy,
    notations: bespokeTheme.notations,
    useOrganizationInvites: bespokeContent.useOrganizationInvites ?? true,
  };

  // add widgets configured here
  const widgets = Widgets(isxContext);
  isxContext.widgets = widgets;
  isxContext.alertTemplates = bespokeTheme.alertTemplates;
  /*isxContext.alertTemplates = bespokeTheme.alertTemplates || {
    "a068dd74-6f8e-41a9-b492-9083ba7ae54d": {
      id: "a068dd74-6f8e-41a9-b492-9083ba7ae54d",
      name: "Multilevel Alerts for Ultrasonic Sensor",
      tagprefix: ["sensor"],
    },
    "a516c834-0b2f-4d71-a42b-c434483b8f8a": {
      id: "a516c834-0b2f-4d71-a42b-c434483b8f8a",
      name: "Alert on Any Lubrication Event",
      tagprefix: ["lube_"],
    },
    "ca2012e0-42d0-494f-b3d3-26f6b163730b": {
      id: "ca2012e0-42d0-494f-b3d3-26f6b163730b",
      name: "Alerts for Available Grease % Volume",
      tagprefix: ["vol_"],
      prefixdata: {
        vol_: {
          name: "Alerts for Available Grease % Volume",
          description: {
            threshold: {
              level1: {
                low: "25% grease remaining in cartridge, order replacement",
              },
              level2: {
                low: "10% grease remaining in cartridge, replace soon",
              },
              level3: {
                low: "5% grease remaining in cartridge, replace ASAP",
              },
            },
          },
        },
      },
      default: true,
    },
    "2a89f671-d351-45cd-a11c-38229e58f0da": {
      id: "2a89f671-d351-45cd-a11c-38229e58f0da",
      name: "Alert for Connection Status",
      tagprefix: ["isxreserved|connected"],
    },
    "f57b25d6-7b99-49b9-a914-b2b9a14ec02f": {
    "id": "f57b25d6-7b99-49b9-a914-b2b9a14ec02f",
    "name": "Auto Lubrication Trigger Event",
    "tagprefix": [
     "sensor"
    ],
    "triggers":{"send_autolube_command":{}}
   }
  };*/

  return (
    <Provider store={store}>
      <CookiesProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <GlobalStyles
              styles={{
                "@keyframes mui-auto-fill": { from: { display: "block" } },
                "@keyframes mui-auto-fill-cancel": {
                  from: { display: "block" },
                },
              }}
            />
            <ISXContext.Provider value={isxContext}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <App />
              </LocalizationProvider>
            </ISXContext.Provider>
          </ThemeProvider>
        </StyledEngineProvider>
      </CookiesProvider>
    </Provider>
  );
};

/*ReactDOM.render(<AppWrapper />, document.getElementById("root"));*/
ReactDOM.render(
  <Router>
    <AppWrapper />
  </Router>,
  document.getElementById("root")
);
